import { useEffect, useRef, useState } from 'react';

import { getScrollParent } from '@utils/dom';

/**
 * Tracks whether a scroll container has been scrolled away from its top.
 *
 * Render the returned `sentinelRef` on an element at the top of the scrollable
 * content; the hook resolves its nearest scrollable ancestor and reports
 * `isScrolled: true` while that ancestor is scrolled down. Works for content
 * that scrolls inside a container (modal, drawer) rather than the window.
 *
 * @param enabled - when `false` no listener is attached and `isScrolled` stays
 *   `false` (useful to skip the work while a feature is off).
 */
export const useScrolledFromTop = (enabled = true) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsScrolled(false);
      return;
    }

    const scrollParent = getScrollParent(sentinelRef.current);
    if (!scrollParent) {
      return;
    }

    const handleScroll = () => setIsScrolled(scrollParent.scrollTop > 0);

    handleScroll();
    scrollParent.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollParent.removeEventListener('scroll', handleScroll);
  }, [enabled]);

  return { sentinelRef, isScrolled };
};
